import React from "react";
import { FocusScope } from "react-aria";
import { Provider, VisuallyHidden } from "react-aria-components";
import { createPortal, flushSync } from "react-dom";
import { t } from "ttag";

import { ClickableBlock, CmsHeaderContent, Dropdown } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";
import { slugify } from "@thelabnyc/thelabui/src/utils/text";

import { ButtonPrimary, ButtonSecondary, Clickable } from "../../Clickables";
import { Logo } from "../../Logo";
import { SearchModal } from "../../SearchModal";
import { Separator } from "../../Separator";
import { Svg } from "../../Svg";
import { clickTrackingValues } from "../clickTrackingValues";
import { navKeyTarget } from "./navKeys";

import breakpoints from "../../../styles/breakpoint.module.scss";
import styles from "./Header.module.scss";

const alSection = "top-nav";
const alClickType = "nav";

const LogoBlock = () => (
    <span className={styles.logo}>
        <Clickable href="/" data-track-label="nav_logo">
            <Logo opticalAlign="center" />
        </Clickable>
    </span>
);

// Hovering (after a short delay) or clicking any dropdown title expands the
// whole header, revealing every dropdown's links in a column under its title.
// `activeId` is the dropdown last hovered, clicked or focused; only its title
// is highlighted while the header is expanded.
const HeaderNavContext = React.createContext<{
    expanded: boolean;
    activeId: string | null;
    press: (id: string) => void;
    activate: (id: string) => void;
    scheduleOpen: (id: string) => void;
    cancelOpen: () => void;
}>({
    expanded: false,
    activeId: null,
    press: () => {},
    activate: () => {},
    scheduleOpen: () => {},
    cancelOpen: () => {},
});

// Must be module-level so StreamField always sees the same component reference.
// A new reference on every render would cause unmount+remount of the trigger
// element, firing onPointerEnter and immediately re-opening the closed panel.
const DropdownBlock = ({ value, id }: { value: Dropdown; id?: string }) => {
    const nav = React.useContext(HeaderNavContext);
    const slug = slugify(value.trigger_text ?? "unknown");
    if (!id) return null;
    const listId = `nav-links-${id}`;
    const active = nav.expanded && nav.activeId === id;

    return (
        <div
            className={styles.navColumn}
            data-nav-id={id}
            data-active={active ? true : undefined}
            // Moving over (or tabbing into) any part of a column, links
            // included, makes it the highlighted one.
            onPointerEnter={(e) => {
                if (e.pointerType === "mouse") nav.activate(id);
            }}
            onFocus={() => nav.activate(id)}
        >
            <Clickable
                className={styles.menuTrigger}
                data-nav-top
                aria-expanded={nav.expanded}
                aria-controls={listId}
                data-active={active ? true : undefined}
                onPointerEnter={(e: React.PointerEvent) => {
                    if (e.pointerType === "mouse") nav.scheduleOpen(id);
                }}
                onPointerLeave={nav.cancelOpen}
                onClick={() => {
                    nav.cancelOpen();
                    nav.press(id);
                }}
                data-track-label={slug}
            >
                {value.trigger_text}
            </Clickable>
            <ul
                id={listId}
                className={styles.navColumnLinks}
                data-nav-links
                data-hidden={nav.expanded ? undefined : true}
            >
                {value.links?.map((link) => (
                    <li key={link.id}>
                        <Clickable
                            simpleLink={link.value}
                            className={styles.navColumnLink}
                            data-nav-link
                            data-click-type={alClickType}
                            data-track-label={`sub_${slug}_${slugify(link.value.display_name ?? "unknown")}`}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
};

const LinkBlock = ({ value }: { value: ClickableBlock }) => {
    switch (value.style) {
        case "secondary":
            return (
                <ButtonSecondary
                    data-click-type={alClickType}
                    simpleLink={value}
                />
            );
        default:
            return (
                <Clickable
                    data-click-type={alClickType}
                    className={styles.menuTrigger}
                    simpleLink={value}
                />
            );
    }
};

// Module-level so StreamField sees stable component references (see
// DropdownBlock).
const headerComponents = {
    link: LinkBlock,
    logo: LogoBlock,
    dropdown: DropdownBlock,
};

const MobileMenuContext = React.createContext<{
    openDropdown: (id: string) => void;
}>({ openDropdown: () => {} });

const NullBlock = () => null;

const MobileLinkBlock = (props: { value: ClickableBlock }) => {
    // Secondary links are rendered as CTA buttons at the bottom of the menu,
    // not as menu rows.
    if (props.value.style === "secondary") return null;
    return (
        <Clickable
            simpleLink={{ ...props.value, display_name: undefined }}
            className={styles.mobileMenuItem}
            data-track-label={`sub_${slugify(props.value.display_name ?? "unknown")}`}
        >
            <span>{props.value.display_name}</span>
            <Svg name="arrow" aria-hidden="true" />
        </Clickable>
    );
};

const MobileMenuButtons = (props: { value: CmsHeaderContent }) => (
    <div className={styles.mobileMenuButtons}>
        {props.value.value.map((item) =>
            item.type === "link" && item.value.style === "secondary" ? (
                <ButtonSecondary
                    key={item.id}
                    simpleLink={item.value}
                    className={styles.mobileMenuButton}
                />
            ) : null,
        )}
        <ButtonPrimary
            href="https://connect.leanin.org"
            className={styles.mobileMenuButton}
            data-track-label={`sub_login`}
        >
            {t`Login`}
            <Svg name="account" aria-hidden="true" />
        </ButtonPrimary>
    </div>
);

const MobileDropdownBlock = (props: { value: Dropdown; id?: string }) => {
    const { openDropdown } = React.useContext(MobileMenuContext);
    const id = props.id;
    if (!id) return null;
    return (
        <Clickable
            type="button"
            className={styles.mobileMenuItem}
            onPress={() => openDropdown(id)}
            aria-haspopup="dialog"
            data-track-label={slugify(props.value.trigger_text ?? "unknown")}
        >
            <span>{props.value.trigger_text}</span>
            <Svg name="arrow" aria-hidden="true" />
        </Clickable>
    );
};

const MobileMenu = (props: {
    value: CmsHeaderContent;
    onClose: () => void;
}) => {
    const [activeDropdownId, setActiveDropdownId] = React.useState<
        string | null
    >(null);

    // The overlay is hidden by CSS at >= tablet width. Without this, a user
    // who opens the menu on mobile and then resizes/rotates past the
    // breakpoint would leave the FocusScope trap active on a display:none
    // element, breaking keyboard access to the rest of the page.
    React.useEffect(() => {
        const mql = window.matchMedia(`(min-width: ${breakpoints.tablet})`);
        const handleChange = (e: MediaQueryListEvent) => {
            if (e.matches) props.onClose();
        };
        mql.addEventListener("change", handleChange);
        return () => mql.removeEventListener("change", handleChange);
    }, [props.onClose]);

    const activeDropdown = props.value.value.find(
        (item) => item.type === "dropdown" && item.id === activeDropdownId,
    );

    return createPortal(
        <FocusScope autoFocus restoreFocus contain>
            <div
                className={styles.mobileMenuOverlay}
                role="dialog"
                aria-modal="true"
                aria-label={
                    activeDropdown?.type === "dropdown"
                        ? (activeDropdown.value.trigger_text ?? undefined)
                        : t`Menu`
                }
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        if (activeDropdownId) {
                            setActiveDropdownId(null);
                        } else {
                            props.onClose();
                        }
                    }
                }}
            >
                {activeDropdown?.type === "dropdown" && (
                    <div className={styles.mobileMenuTopBar}>
                        <button
                            type="button"
                            className={styles.mobileBackButton}
                            onClick={() => setActiveDropdownId(null)}
                            aria-label={t`Back`}
                            data-click-type="nav"
                            data-track-label={`mobile_nav_back_${slugify(activeDropdown.value.trigger_text ?? "unknown")}`}
                        >
                            <Svg name="arrow" aria-hidden="true" />
                        </button>
                        <span className={styles.mobileMenuTitle}>
                            {activeDropdown.value.trigger_text}
                        </span>
                    </div>
                )}
                {activeDropdown?.type === "dropdown" ? (
                    activeDropdown.value.links?.map((link) => (
                        <MobileLinkBlock
                            key={link.id}
                            value={{ ...link.value, style: "default" }}
                        />
                    ))
                ) : (
                    <MobileMenuContext.Provider
                        value={{ openDropdown: setActiveDropdownId }}
                    >
                        <StreamField
                            tagName={React.Fragment}
                            value={props.value.value}
                            components={{
                                link: MobileLinkBlock,
                                logo: NullBlock,
                                dropdown: MobileDropdownBlock,
                            }}
                        />
                        <MobileMenuButtons value={props.value} />
                    </MobileMenuContext.Provider>
                )}
            </div>
        </FocusScope>,
        document.body,
    );
};

const OPEN_DELAY_MS = 400;
const CLOSE_DELAY_MS = 500;
// Spacing between nav titles: preferred minimum, and the floor when the
// screen is too narrow for the links to fit without wrapping.
const NAV_MIN_GAP = 24;
const NAV_SMALLEST_GAP = 16;

export const Header = (props: { value: CmsHeaderContent }) => {
    const [searchOpen, setSearchOpen] = React.useState(false);
    const [navExpanded, setNavExpanded] = React.useState(false);
    const [activeNavId, setActiveNavId] = React.useState<string | null>(null);
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const headerRef = React.useRef<HTMLElement>(null);
    const navItemsRef = React.useRef<HTMLElement>(null);
    const spacerRef = React.useRef<HTMLSpanElement>(null);
    const openTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    const cancelOpen = React.useCallback(() => {
        if (openTimerRef.current !== null) {
            clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
    }, []);
    const cancelClose = React.useCallback(() => {
        if (closeTimerRef.current !== null) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);
    const expandNav = React.useCallback(() => {
        cancelClose();
        setNavExpanded(true);
    }, [cancelClose]);
    const collapseNav = React.useCallback(() => {
        cancelOpen();
        cancelClose();
        setNavExpanded(false);
        setActiveNavId(null);
    }, [cancelOpen, cancelClose]);

    React.useEffect(
        () => () => {
            cancelOpen();
            cancelClose();
        },
        [cancelOpen, cancelClose],
    );

    // Listen on the document: when the nav was opened by hovering, focus is
    // usually outside the header, so a header-level handler would miss it.
    React.useEffect(() => {
        if (!navExpanded) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            // Don't strand focus on a link that's about to be hidden: return
            // it to that column's title.
            const link =
                document.activeElement instanceof HTMLElement &&
                document.activeElement.closest("[data-nav-link]");
            if (link) {
                link.closest("[data-nav-id]")
                    ?.querySelector<HTMLElement>("[data-nav-top]")
                    ?.focus();
            }
            collapseNav();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [navExpanded, collapseNav]);

    // Any click outside the link columns (the overlay, the panel background,
    // the rest of the header) collapses the nav. Titles and links handle
    // their own clicks.
    React.useEffect(() => {
        if (!navExpanded) return;
        const onClick = (e: MouseEvent) => {
            if (
                e.target instanceof Element &&
                e.target.closest("[data-nav-id]")
            ) {
                return;
            }
            collapseNav();
        };
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, [navExpanded, collapseNav]);

    const onNavKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (!(e.target instanceof HTMLElement)) return;
        const tops = Array.from(
            e.currentTarget.querySelectorAll<HTMLElement>("[data-nav-top]"),
        );
        const column = e.target.closest<HTMLElement>("[data-nav-id]");
        const top =
            column?.querySelector<HTMLElement>("[data-nav-top]") ?? e.target;
        const links = column
            ? Array.from(
                  column.querySelectorAll<HTMLElement>("[data-nav-link]"),
              )
            : [];
        const topIndex = tops.indexOf(top);
        if (topIndex === -1) return;
        const next = navKeyTarget(e.key, {
            topIndex,
            topCount: tops.length,
            linkIndex: links.indexOf(e.target),
            linkCount: links.length,
        });
        if (!next) return;
        e.preventDefault();
        if (next.kind === "top") {
            tops[next.index].focus();
            return;
        }
        // Links are hidden while collapsed; render them before focusing.
        const id = column?.dataset.navId;
        if (!navExpanded && id) {
            flushSync(() => {
                setActiveNavId(id);
                expandNav();
            });
        }
        links[next.index].focus();
    };

    const navContext = React.useMemo(
        () => ({
            expanded: navExpanded,
            activeId: activeNavId,
            // Clicking the highlighted title closes the nav; clicking any
            // other title opens it (or just moves the highlight there).
            press: (id: string) => {
                if (navExpanded && activeNavId === id) {
                    collapseNav();
                } else {
                    setActiveNavId(id);
                    expandNav();
                }
            },
            activate: (id: string) => {
                if (navExpanded) setActiveNavId(id);
            },
            scheduleOpen: (id: string) => {
                cancelOpen();
                // Already open: the column's own pointerenter moves the
                // highlight, no delay needed.
                if (navExpanded) return;
                openTimerRef.current = setTimeout(() => {
                    setActiveNavId(id);
                    expandNav();
                }, OPEN_DELAY_MS);
            },
            cancelOpen,
        }),
        [navExpanded, activeNavId, collapseNav, expandNav, cancelOpen],
    );

    // Dropdowns (in CMS order) on the left, logo centred, and plain links
    // then CTA buttons on the right, wherever the logo sits in the CMS order.
    type HeaderItem = CmsHeaderContent["value"][number];
    const isCta = (item: HeaderItem) =>
        item.type === "link" && item.value.style === "secondary";
    const logoItems = props.value.value.filter((item) => item.type === "logo");
    const navItems = props.value.value.filter(
        (item) => item.type === "dropdown",
    );
    const rightItems = [
        ...props.value.value.filter(
            (item) => item.type === "link" && !isCta(item),
        ),
        ...props.value.value.filter(isCta),
    ];

    // Pick one gap between nav titles that's just wide enough for every link
    // column to clear the next title, capped by the space available. Links
    // stay laid out while hidden, so this works before the nav is opened.
    React.useLayoutEffect(() => {
        const header = headerRef.current;
        const items = navItemsRef.current;
        const spacer = spacerRef.current;
        if (!header || !items || !spacer) return;
        const update = () => {
            const columns = Array.from(items.children) as HTMLElement[];
            // Hidden on mobile, or nothing to space out.
            if (columns.length < 2 || items.offsetWidth === 0) return;
            const titleWidths = columns.map((column) => column.offsetWidth);
            let needed = NAV_MIN_GAP;
            columns.slice(0, -1).forEach((column, i) => {
                const list =
                    column.querySelector<HTMLElement>("[data-nav-links]");
                if (!list) return;
                // Measure the links' natural width, ignoring the wrap cap.
                list.style.maxWidth = "none";
                needed = Math.max(
                    needed,
                    list.offsetWidth - titleWidths[i] + NAV_MIN_GAP,
                );
                list.style.maxWidth = "";
            });
            const titlesWidth = titleWidths.reduce((a, b) => a + b, 0);
            const available =
                items.offsetWidth + spacer.offsetWidth - titlesWidth;
            const gap = Math.max(
                NAV_SMALLEST_GAP,
                Math.min(needed, available / (columns.length - 1)),
            );
            header.style.setProperty("--nav-gap", `${Math.floor(gap)}px`);
        };
        update();
        // Guarded for jsdom, which lacks the Font Loading API.
        if ("fonts" in document) void document.fonts.ready.then(update);
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(update);
        observer.observe(header);
        return () => observer.disconnect();
    }, []);

    // Size the expanded panel's background to the tallest link column.
    // Measured rather than computed because links wrap when --nav-gap had to
    // shrink.
    React.useLayoutEffect(() => {
        const header = headerRef.current;
        if (!header || !navExpanded) return;
        const lists = header.querySelectorAll<HTMLElement>("[data-nav-links]");
        const update = () => {
            const tallest = Math.max(
                0,
                ...Array.from(lists, (list) => list.offsetHeight),
            );
            header.style.setProperty("--nav-panel-height", `${tallest}px`);
        };
        update();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(update);
        lists.forEach((list) => observer.observe(list));
        return () => observer.disconnect();
    }, [navExpanded]);

    return (
        <Provider values={clickTrackingValues("nav")}>
            <HeaderNavContext.Provider value={navContext}>
                <header
                    ref={headerRef}
                    className={styles.header}
                    data-track-section={alSection}
                    onBlur={(e) => {
                        if (
                            navExpanded &&
                            !e.currentTarget.contains(e.relatedTarget)
                        ) {
                            collapseNav();
                        }
                    }}
                >
                    <div className={styles.root}>
                        <div className={styles.navGroup}>
                            <Clickable
                                className={styles.iconButton}
                                data-click-type={alClickType}
                                data-track-label="search"
                                onPress={() => {
                                    setMobileMenuOpen(false);
                                    collapseNav();
                                    setSearchOpen(true);
                                }}
                            >
                                <VisuallyHidden>{t`Search`}</VisuallyHidden>
                                <Svg name={searchOpen ? "close" : "search"} />
                            </Clickable>
                            <SearchModal
                                isOpen={searchOpen}
                                onOpenChange={setSearchOpen}
                                triggerRef={headerRef}
                            />
                            <Separator orientation="vertical" />
                            <nav
                                ref={navItemsRef}
                                className={styles.navItems}
                                aria-label={t`Main`}
                                onKeyDown={onNavKeyDown}
                            >
                                <StreamField
                                    tagName={React.Fragment}
                                    value={navItems}
                                    components={headerComponents}
                                />
                            </nav>
                            <span
                                ref={spacerRef}
                                className={styles.spacer}
                                aria-hidden
                            />
                        </div>
                        <StreamField
                            tagName={React.Fragment}
                            value={logoItems}
                            components={headerComponents}
                        />
                        <div className={styles.ctaGroup}>
                            <StreamField
                                tagName={React.Fragment}
                                value={rightItems}
                                components={headerComponents}
                            />
                            <Separator orientation="vertical" />
                            <Clickable
                                className={concatClassNames([
                                    styles.iconButton,
                                    styles.hideOnMobile,
                                ])}
                                href="https://connect.leanin.org"
                                data-click-type={alClickType}
                                data-track-label="account"
                            >
                                <VisuallyHidden>{t`Account`}</VisuallyHidden>
                                <Svg name="account" aria-hidden />
                            </Clickable>
                            <Clickable
                                className={concatClassNames([
                                    styles.showOnMobile,
                                    styles.iconButton,
                                    styles.openMobileMenu,
                                ])}
                                onPress={() => {
                                    if (!mobileMenuOpen) collapseNav();
                                    setMobileMenuOpen(!mobileMenuOpen);
                                }}
                                data-click-type={alClickType}
                                data-track-label="mobile_menu"
                                aria-expanded={mobileMenuOpen}
                            >
                                <VisuallyHidden>
                                    {mobileMenuOpen ? t`Close menu` : t`Menu`}
                                </VisuallyHidden>
                                <Svg
                                    aria-hidden
                                    name={
                                        mobileMenuOpen ? "close" : "hamburger"
                                    }
                                />
                            </Clickable>
                        </div>
                    </div>
                    {navExpanded && (
                        <>
                            <div className={styles.navExpansion} />
                            <div
                                className={styles.dropdownOverlay}
                                onPointerEnter={() => {
                                    cancelClose();
                                    closeTimerRef.current = setTimeout(
                                        collapseNav,
                                        CLOSE_DELAY_MS,
                                    );
                                }}
                                onPointerLeave={cancelClose}
                            />
                        </>
                    )}
                </header>
                {mobileMenuOpen && (
                    <MobileMenu
                        value={props.value}
                        onClose={() => setMobileMenuOpen(false)}
                    />
                )}
            </HeaderNavContext.Provider>
        </Provider>
    );
};
